import { useDirectusStore } from "@/modules/directus/store/directus";
import { aggregate, createItem, deleteItem, readItems } from "@directus/sdk";
import { defineStore } from "pinia";
import { ref } from "vue";
import LikeInterface, { LikeCountInterface } from "../interfaces/LikeInterface";
import ArticleInterface from "@/modules/journal/interfaces/ArticleInterface";
import { useAuthStore } from "@/modules/user/store/auth";

export const useLikeStore = defineStore("like", () => {
  const directusStore = useDirectusStore();

  const likes = ref<LikeCountInterface[]>([]);

  const userLikes = ref<LikeInterface[]>([]);

  const getLikes = (
    articles: Pick<ArticleInterface, "id">[],
    force = false
  ) => {
    const missingArticles = articles.filter(
      (article) => !likes.value.find((like) => like.article === article.id)
    );
    if (missingArticles.length > 0 || force) {
      return directusStore.city
        ?.request(
          aggregate("likes", {
            aggregate: { count: "*" },
            groupBy: ["article"],
            query: {
              filter: {
                article: { _in: articles.map((article) => article.id) },
              },
            },
          })
        )
        .then((response) => {
          articles.forEach((article) => {
            likes.value = likes.value.filter(
              (like) => like.article !== article.id
            );
            if (response.find((like) => like.article === article.id)) {
              likes.value.push({
                article: article.id,
                count: response.find((like) => like.article === article.id)
                  ?.count as unknown as number,
              });
            } else {
              likes.value.push({ article: article.id, count: 0 });
            }
          });
          return likes.value;
        });
    } else {
      return Promise.resolve(likes.value);
    }
  };

  const getUserLikes = () => {
    return directusStore.city
      ?.request(
        readItems("likes", {
          filter: {
            user_created: {
              external_identifier: {
                _eq: useAuthStore().Pool.getCurrentUser()?.getUsername(),
              },
            },
          },
        })
      )
      .then((response) => {
        userLikes.value = response as LikeInterface[];
        return userLikes.value;
      });
  };

  const toggleLike = async (article: Pick<ArticleInterface, "id">) => {
    const existingLikes = await getUserLikes();
    const existingLike = existingLikes?.find(
      (like) => like.article === article.id
    );
    if (existingLike) {
      await directusStore.city?.request(deleteItem("likes", existingLike.id));
    } else {
      await directusStore.city?.request(
        createItem("likes", {
          article: article.id,
        })
      );
    }

    await getLikes([article], true);

    return getUserLikes();
  };

  if (useAuthStore().Pool.getCurrentUser()) {
    getUserLikes();
  }

  return {
    getLikes,
    toggleLike,
    getUserLikes,
    userLikes,
    likes,
  };
});
